import React, { memo } from 'react';
import { Map } from 'immutable';

const PureDeepValue = Component => {
  const checkProps = [
    'value',
    'className',
    'deep',
    'prefix',
    'emptyValue',
    'name',
    'disabled',
    'autoFocus',
    'loading',
    'status',
    'isLoading',
  ];

  // Applies the same defaults `DeepValueWrapped.defaultProps` used to give the class - resolved
  // explicitly here (rather than via `defaultProps` on the memoized export) so `arePropsEqual`
  // and the render function are guaranteed to see the same, already-defaulted values, matching
  // what `this.props` already had by the time the original `shouldComponentUpdate` read it.
  const withDefaults = props => ({
    deep: false,
    emptyValue: '',
    prefix: '',
    ...props,
  });

  const arePropsEqual = (previousRawProps, nextRawProps) => {
    const previousProps = withDefaults(previousRawProps);
    const nextProps = withDefaults(nextRawProps);

    if (!previousProps.deep) return false;
    if (checkProps.some(property => nextProps[property] !== previousProps[property])) {
      return false;
    }

    if (previousProps.root === nextProps.root) return true;

    const previousValue = getProperty(
      previousProps.root,
      getPath(previousProps.prefix, previousProps.name),
      previousProps.emptyValue,
    );
    const nextValue = getProperty(nextProps.root, getPath(nextProps.prefix, nextProps.name), nextProps.emptyValue);

    return previousValue === nextValue;
  };

  /**
   * DeepValue decorator.
   *
   * @param props Any extra props will passed into children.
   * @param [props.deep=false] {bool|String} If True - activate deep value find.
   *     If string - use as name of property to pass value.
   * @param [props.prefix=''] {string}.
   * @param props.name {string} Name of input value.
   * @param props.root {*} If `deep==true` should be Map.
   */
  const DeepValueWrapped = rawProps => {
    const { deep, prefix, emptyValue, ...rest } = withDefaults(rawProps);
    const { name, root, ...realRest } = rest;

    if (!deep) return <Component {...rest} />;
    checkIfMap(root);
    if (prefix) {
      const realName = `${prefix}.${name}`;
      const path = prefix.split('.').concat(name);

      checkIfHasSuchPath(root, path, emptyValue);

      return <Component {...realRest} name={realName} {...getPropertyObject(deep, root, path, emptyValue)} />;
    }

    return <Component {...realRest} name={name} {...getPropertyObject(deep, root, [name], emptyValue)} />;
  };

  DeepValueWrapped.displayName = `PureDeepValue(${Component.displayName || Component.name})`;

  return memo(DeepValueWrapped, arePropsEqual);
};

export { PureDeepValue };

const DeepValue = Component => {
  /**
   * DeepValue decorator.
   *
   * @param props Any extra props will passed into children.
   * @param [props.deep=false] {bool|String} If True - activate deep value find.
   *     If string - use as name of property to pass value.
   * @param [props.prefix=''] {string}.
   * @param props.name {string} Name of input value.
   * @param props.root {*} If `deep==true` should be Map.
   */
  const DeepValueWrapped = props => {
    const { deep = false, prefix = '', emptyValue = '', ...rest } = props;
    const { name, root, ...realRest } = rest;

    if (!deep) return <Component {...rest} />;
    checkIfMap(root);
    if (prefix) {
      const realName = `${prefix}.${name}`;
      const path = prefix.split('.').concat(name);

      checkIfHasSuchPath(root, path, emptyValue);

      return <Component {...realRest} name={realName} {...getPropertyObject(deep, root, path, emptyValue)} />;
    }

    return <Component {...realRest} name={name} {...getPropertyObject(deep, root, [name], emptyValue)} />;
  };

  DeepValueWrapped.displayName = `PureDeepValue(${Component.displayName || Component.name})`;

  return DeepValueWrapped;
};

export default DeepValue;

const checkIfMap = value => {
  if (process.env.NODE_ENV !== 'production') {
    if (!Map.isMap(value)) {
      console.error(`DeepValue decorator error.
Need Immutable.Map as 'root' property. But has something else.`);
    }
  }
};

const checkIfHasSuchPath = (value, path, emptyValue) => {
  if (emptyValue === false) {
    if (process.env.NODE_ENV !== 'production') {
      if (!value.hasIn(path)) {
        console.error(`DeepValue decorator error.
Need root to has "${path.join('.')}". But it has not.`);
      }
    }
  }
};

const getPropertyObject = (deep, root, path, emptyValue) => {
  if (typeof deep === 'string') return { [deep]: root.getIn(path, emptyValue) };

  return { value: root.getIn(path, emptyValue) };
};

const getProperty = (root, path, emptyValue) => {
  if (!root) return null;

  return root.getIn(path, emptyValue);
};

const getPath = (prefix, name) => {
  if (prefix) return prefix.split('.').concat(name);

  return [name];
};
